import { UzButton, UzDialog, UzInput, useBranding } from '@utazzo/ui';
import { ArrowLeft, Ban, Pencil, Printer } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { CreditNotePanel } from '../components/CreditNotePanel';
import { InvoiceAdminPanel } from '../components/InvoiceAdminPanel';
import { SupplierSettlementPanel } from '../components/SupplierSettlementPanel';
import {
  amountToWords,
  formatDateDMY,
  formatInrPaise,
  invoiceParticulars,
} from '../invoices.helpers';
import { useCancelInvoice, useInvoice } from '../use-invoices';

import type { InvoiceDetail } from '@utazzo/shared';

/**
 * Full-page GST tax-invoice sheet (route /invoices/:id). Faithfully replicates
 * the legacy `invoicebillwithgst.aspx` layout: a single bordered outer table
 * with thin black borders, compact cells and print-oriented styling.
 *
 * The print stylesheet scopes the printed output to `#invoice-sheet` only,
 * hiding the app chrome (sidebar/topbar) so the sheet prints (or "Save as
 * PDF"s) on its own. All visual styling is scoped to `#invoice-sheet` so it
 * does not leak into the rest of the admin app.
 */

/**
 * Scoped CSS: (1) the bordered-table invoice look, all under `#invoice-sheet`;
 * (2) the `@media print` isolation that hides everything but the sheet.
 */
const SHEET_CSS = `
#invoice-sheet {
  color: #000;
  background: #fff;
  font-family: Calibri, Candara, Segoe, "Segoe UI", Arial, sans-serif;
  font-size: 13px;
  line-height: 1.35;
}
#invoice-sheet .inv-title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
}
#invoice-sheet .inv-logo {
  width: 240px;
  max-width: 60%;
  height: auto;
  margin: 4px auto 6px;
  display: block;
}
#invoice-sheet table.inv {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}
#invoice-sheet table.inv > tbody > tr > td {
  border: 1px solid #000;
  padding: 3px 6px;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
#invoice-sheet .inv-c { text-align: center; }
#invoice-sheet .inv-r { text-align: right; }
#invoice-sheet .inv-b { font-weight: 700; }
#invoice-sheet table.inv-bank {
  border-collapse: collapse;
  border: 0;
  width: 100%;
  table-layout: fixed;
}
#invoice-sheet table.inv-bank td {
  border: 0;
  padding: 1px 0;
  vertical-align: top;
}
/* Narrow label column so the value column (the actual details) gets more width. */
#invoice-sheet table.inv-bank td.k { font-weight: 700; width: 42%; }
#invoice-sheet table.inv-bank td.s { width: 10px; padding: 0 6px; font-weight: 700; }
/* Multiple default banks (e.g. ICICI + HDFC) print side by side, not stacked,
   with a vertical separator between them. */
#invoice-sheet .inv-banks-row {
  display: flex;
  flex-wrap: wrap;
  margin-top: 6px;
}
#invoice-sheet .inv-bank-col { flex: 1 1 45%; min-width: 240px; padding: 0 16px; }
#invoice-sheet .inv-bank-col:first-child { padding-left: 0; }
#invoice-sheet .inv-bank-col + .inv-bank-col { border-left: 1px solid #999; }
#invoice-sheet .inv-seal {
  height: 100px;
  max-width: 160px;
  object-fit: contain;
  display: block;
  margin: 24px auto;
}
#invoice-sheet .inv-note { font-size: 10px; }
#invoice-sheet .inv-terms-cell { vertical-align: top; text-align: left; }
#invoice-sheet .inv-terms-body {
  font-size: 10px;
  line-height: 1.5;
  white-space: pre-line;
  margin-top: 2px;
}

@media print {
  body * { visibility: hidden; }
  #invoice-sheet, #invoice-sheet * { visibility: visible; }
  #invoice-sheet {
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 24px;
    box-shadow: none !important;
    border: none !important;
    max-width: none !important;
    width: 100%;
  }
  .invoice-no-print { display: none !important; }
  @page { margin: 12mm; }
}
`;

/** Human-readable place of supply from the enum + a state name when we have one. */
const placeOfSupplyLabel = (inv: InvoiceDetail): string => {
  const stateName = inv.buyer?.stateName ?? inv.billingEntity.stateName ?? null;
  switch (inv.placeOfSupply) {
    case 'INTRA':
      return `Intra-state${stateName ? ` (${stateName})` : ''}`;
    case 'INTER':
      return `Inter-state${stateName ? ` (${stateName})` : ''}`;
    case 'EXPORT':
      return 'Export / Out of India';
    default:
      return inv.placeOfSupply;
  }
};

/** Document title, matching the legacy heading rules. */
const docTitleFor = (status: InvoiceDetail['status']): string => {
  if (status === 'PROFORMA') return 'Proforma Invoice';
  if (status === 'CANCELLED') return 'Tax Invoice (Cancelled)';
  return 'Tax Invoice';
};

export const InvoiceDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const invoiceQuery = useInvoice(id);
  const branding = useBranding();
  const cancel = useCancelInvoice();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  if (invoiceQuery.isLoading) {
    return <div className="p-8 text-sm text-zinc-500">Loading invoice...</div>;
  }
  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-sm text-red-600">Could not load this invoice.</p>
        <Link to="/invoices" className="text-primary text-sm underline">
          Back to invoices
        </Link>
      </div>
    );
  }

  const inv = invoiceQuery.data;
  const entity = inv.billingEntity;
  const buyer = inv.buyer;
  // Banks to print: the one chosen on the invoice, else all active banks of the
  // GST account (ICICI + HDFC by default) - resolved by the backend.
  const banks = inv.banks;
  const isProforma = inv.status === 'PROFORMA';
  const docTitle = docTitleFor(inv.status);

  // Trip details shown in the particulars box (item 8).
  const passengerNames = inv.passengers
    .map((p) => [p.title, p.firstName, p.lastName].filter(Boolean).join(' ').trim())
    .filter(Boolean)
    .join(', ');
  const hasTripDetails =
    inv.departure ||
    inv.destination ||
    inv.departureDate ||
    inv.returnDate ||
    passengerNames ||
    inv.products.length > 0;

  const grandTotalNum = Number(inv.grandTotalPaise);
  const tcsNum = Number(inv.tcsPaise);
  // Particulars amounts that reconcile line(s) + (GST rows, when shown) + TCS = Total.
  const { particularsTotalPaise, displayLinePaise } = invoiceParticulars({
    grandTotalPaise: inv.grandTotalPaise,
    cgstPaise: inv.cgstPaise,
    sgstPaise: inv.sgstPaise,
    igstPaise: inv.igstPaise,
    tcsPaise: inv.tcsPaise,
    showGstBifurcation: inv.showGstBifurcation,
    lineAmountsPaise: inv.lines.map((l) => l.amountPaise),
  });
  // TCS rate off its base (the gross incl GST) for the "TCS @ x%" label.
  const tcsBase = grandTotalNum - tcsNum;
  const tcsRate = tcsNum > 0 && tcsBase > 0 ? Math.round((tcsNum * 10000) / tcsBase) / 100 : 0;
  // Units for the particulars line = pax count (booked pax, else passenger rows, else 1).
  const paxUnits = inv.noOfPax ?? (inv.passengers.length || 1);

  // Customer (buyer) block, falling back to the plain customerName.
  const customerName = buyer?.name ?? inv.customerName ?? '-';
  const customerCityState = buyer ? [buyer.city, buyer.stateName].filter(Boolean).join(', ') : '';
  const customerGst = buyer?.gstNumber ?? inv.buyerGstin ?? null;
  const customerStateCode = buyer?.stateCode ?? inv.buyerStateCode ?? null;

  const entityCityState = [entity.city, entity.stateName].filter(Boolean).join(', ');

  // Invoice logo: the billing entity's own logo, else the global brand logo
  // (both come from the settings). Renders the header masthead like the legacy.
  const logoUrl = entity.logoUrl ?? branding.logoUrl ?? null;

  return (
    <div className="space-y-4">
      <style>{SHEET_CSS}</style>

      {/* Toolbar (never printed). Phones: compact buttons that wrap instead of
          running off-screen. */}
      <div className="invoice-no-print flex items-center justify-between max-sm:flex-wrap max-sm:gap-2">
        <Link
          to="/invoices"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 max-sm:text-xs max-sm:whitespace-nowrap"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to invoices
        </Link>
        <div className="flex items-center gap-2 max-sm:flex-wrap">
          {/* Invoices are editable at any stage (proforma, tax invoice, cancelled). */}
          <Link
            to={`/invoices/${inv.id}/edit`}
            className="border-input hover:bg-accent hover:text-accent-foreground inline-flex h-10 items-center gap-1.5 rounded-md border bg-white px-4 py-2 text-sm font-medium max-sm:h-8 max-sm:px-3 max-sm:text-xs"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Link>
          {inv.status !== 'CANCELLED' && (
            <UzButton
              variant="outline"
              className="max-sm:h-8 max-sm:px-3 max-sm:text-xs"
              leftIcon={<Ban className="h-4 w-4" />}
              onClick={() => setCancelOpen(true)}
            >
              Cancel invoice
            </UzButton>
          )}
          <UzButton
            className="max-sm:h-8 max-sm:px-3 max-sm:text-xs"
            leftIcon={<Printer className="h-4 w-4" />}
            onClick={() => window.print()}
          >
            Print / Save as PDF
          </UzButton>
        </div>
      </div>

      <UzDialog
        open={cancelOpen}
        onOpenChange={(o) => {
          setCancelOpen(o);
          if (!o) setCancelReason('');
        }}
        title="Cancel this invoice?"
        description="The invoice is fully cancelled (status CANCELLED) and kept for the record. No further payments can be added. This mirrors the legacy cancel."
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="outline" onClick={() => setCancelOpen(false)}>
              Keep invoice
            </UzButton>
            <UzButton
              variant="destructive"
              loading={cancel.isPending}
              onClick={() =>
                cancel.mutate(
                  { id: inv.id, reason: cancelReason.trim() || undefined },
                  { onSuccess: () => setCancelOpen(false) },
                )
              }
            >
              Cancel invoice
            </UzButton>
          </div>
        }
      >
        <UzInput
          label="Reason (optional)"
          placeholder="e.g. Booking cancelled by customer"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </UzDialog>

      {/* The printable sheet - legacy bordered tax-invoice layout. */}
      <div
        id="invoice-sheet"
        className="mx-auto max-w-3xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <table className="inv">
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <tbody>
            {/* Title tag row. */}
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                {docTitle}
              </td>
              <td className="inv-b inv-r">Original For Receiver</td>
            </tr>

            {/* Masthead: brand logo + legal name (from settings). */}
            <tr>
              <td colSpan={4} className="inv-c">
                {logoUrl && <img className="inv-logo" src={logoUrl} alt={entity.legalName} />}
                <div className="inv-b">{entity.legalName}</div>
              </td>
            </tr>

            {/* Header: customer (left) + billing entity (right). */}
            <tr>
              <td colSpan={2}>
                <div className="inv-b">Customer Name:</div>
                <div>{customerName}</div>
                {buyer?.addressLine1 && <div>{buyer.addressLine1}</div>}
                {customerCityState && <div>{customerCityState}</div>}
                {customerGst && (
                  <div>
                    <span className="inv-b">GST No.:</span> {customerGst}
                  </div>
                )}
                {customerStateCode && (
                  <div>
                    <span className="inv-b">State Code:</span> {customerStateCode}
                  </div>
                )}
              </td>
              <td colSpan={2} className="inv-r">
                {entity.addressLine1 && <div>{entity.addressLine1}</div>}
                {entity.addressLine2 && <div>{entity.addressLine2}</div>}
                {entityCityState && <div>{entityCityState}</div>}
                {entity.phone && (
                  <div>
                    <span className="inv-b">Contact No.:</span> {entity.phone}
                  </div>
                )}
                {entity.email && (
                  <div>
                    <span className="inv-b">Email:</span> {entity.email}
                  </div>
                )}
                {entity.cin && (
                  <div>
                    <span className="inv-b">CIN No.</span> {entity.cin}
                  </div>
                )}
                <div>
                  <span className="inv-b">GST No.:</span> {entity.gstin}
                </div>
                {entity.stateCode && (
                  <div>
                    <span className="inv-b">State Code:</span> {entity.stateCode}
                  </div>
                )}
              </td>
            </tr>

            {/* Date / Invoice No. */}
            <tr>
              <td className="inv-b">Date:</td>
              <td>{formatDateDMY(inv.createdAt)}</td>
              <td className="inv-b">Invoice No.:</td>
              <td>{inv.invoiceNumber}</td>
            </tr>

            {/* Place of Supply / HSN. */}
            <tr>
              <td className="inv-b">Place of Supply:</td>
              <td>{placeOfSupplyLabel(inv)}</td>
              <td className="inv-b">HSN Code:</td>
              <td>{inv.hsn}</td>
            </tr>

            {/* Reverse Charge / Pan No. */}
            <tr>
              <td className="inv-b">Reverse Charge:</td>
              <td>No</td>
              <td className="inv-b">Pan No.:</td>
              <td>{entity.pan ?? '-'}</td>
            </tr>

            {/* Trip details (item 8): departure/destination, travel dates, pax. */}
            {hasTripDetails && (
              <>
                <tr>
                  <td className="inv-b">Departure:</td>
                  <td>{inv.departure ?? '-'}</td>
                  <td className="inv-b">Destination:</td>
                  <td>{inv.destination ?? '-'}</td>
                </tr>
                <tr>
                  <td className="inv-b">Travel Date:</td>
                  <td>{inv.departureDate ? formatDateDMY(inv.departureDate) : '-'}</td>
                  <td className="inv-b">Return Date:</td>
                  <td>{inv.returnDate ? formatDateDMY(inv.returnDate) : '-'}</td>
                </tr>
                {passengerNames && (
                  <tr>
                    <td className="inv-b">Passenger(s):</td>
                    <td colSpan={3}>{passengerNames}</td>
                  </tr>
                )}
              </>
            )}

            {/* Particulars header. */}
            <tr>
              <td className="inv-b inv-c">S.No.</td>
              <td className="inv-b inv-c">Particulars</td>
              <td className="inv-b inv-c">Units</td>
              <td className="inv-b inv-c">Amount</td>
            </tr>

            {/* Particulars line(s). Legacy invoices often have no explicit line
                items, which left the box blank (esp. when GST breakup is off);
                fall back to the selected travel services + the sale amount so the
                customer always sees what they are paying for. */}
            {inv.lines.length > 0 ? (
              inv.lines.map((line, i) => (
                <tr key={i}>
                  <td className="inv-b inv-c">{i + 1}</td>
                  <td className="inv-b">{line.description}</td>
                  <td className="inv-c">{line.quantity}</td>
                  <td className="inv-r">{formatInrPaise(String(displayLinePaise[i] ?? 0))}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="inv-b inv-c">1</td>
                <td className="inv-b">
                  {inv.products.length > 0
                    ? inv.products.map((p) => <div key={p.code}>{p.name}</div>)
                    : 'Travel services'}
                </td>
                <td className="inv-c">{paxUnits}</td>
                <td className="inv-r">{formatInrPaise(String(particularsTotalPaise))}</td>
              </tr>
            )}

            {/* Amount breakdown. With GST broken out, the particulars line is the
                pre-tax base (sale) price, then CGST/SGST/IGST + TCS reconcile to the
                Total. With GST hidden, the particulars line is the services lump (GST
                baked in) and only TCS + Total show below. TCS is always its own row. */}
            {inv.showGstBifurcation &&
              inv.taxLines.map((tax, i) => (
                <tr key={i}>
                  <td colSpan={3} className="inv-b inv-c">
                    {tax.taxType} @ {Number(tax.ratePercent)}%
                  </td>
                  <td className="inv-r">{formatInrPaise(tax.amountPaise)}</td>
                </tr>
              ))}
            {inv.tcsPaise !== '0' && (
              <tr>
                <td colSpan={3} className="inv-b inv-c">
                  TCS @ {tcsRate}%
                </td>
                <td className="inv-r">{formatInrPaise(inv.tcsPaise)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={3} className="inv-b inv-c">
                Total Amount
              </td>
              <td className="inv-b inv-r">{formatInrPaise(inv.grandTotalPaise)}</td>
            </tr>
            {/* Amount in words. */}
            <tr>
              <td colSpan={4}>
                <span className="inv-b">Total Amount in Word:</span>{' '}
                {amountToWords(inv.grandTotalPaise)}
              </td>
            </tr>

            {/* Bank account details. Shows the invoice's chosen bank, or all
                active banks of the GST account (ICICI + HDFC) when none chosen. */}
            {banks.length > 0 && (
              <>
                <tr>
                  <td colSpan={4} className="inv-b inv-c">
                    Bank Account Details
                  </td>
                </tr>
                <tr>
                  <td colSpan={4}>
                    {/* Each default bank prints as its own self-contained column,
                        side by side - entity / payment mode / beneficiary repeat
                        in every column. */}
                    <div className="inv-banks-row">
                      {banks.map((b, bi) => (
                        <div className="inv-bank-col" key={bi}>
                          {banks.length > 1 && <div className="inv-b">{b.label}</div>}
                          <table className="inv-bank">
                            <tbody>
                              <tr>
                                <td className="k">Entity Name</td>
                                <td className="s">:</td>
                                <td>{entity.legalName}</td>
                              </tr>
                              <tr>
                                <td className="k">Payment Mode</td>
                                <td className="s">:</td>
                                <td>NEFT / RTGS / Account Transfer</td>
                              </tr>
                              <tr>
                                <td className="k">Beneficiary Name</td>
                                <td className="s">:</td>
                                <td>{entity.legalName}</td>
                              </tr>
                              <tr>
                                <td className="k">Bank Name where account is held</td>
                                <td className="s">:</td>
                                <td>{b.bankName}</td>
                              </tr>
                              {b.branchName && (
                                <tr>
                                  <td className="k">Branch</td>
                                  <td className="s">:</td>
                                  <td>{b.branchName}</td>
                                </tr>
                              )}
                              <tr>
                                <td className="k">Bank Account Number</td>
                                <td className="s">:</td>
                                <td>{b.accountNumber}</td>
                              </tr>
                              {b.ifsc && (
                                <tr>
                                  <td className="k">IFSC Code</td>
                                  <td className="s">:</td>
                                  <td>{b.ifsc}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              </>
            )}

            {/* Terms & Conditions (left) beside the Company Seal box (right). */}
            <tr>
              <td colSpan={3} className="inv-terms-cell">
                {entity.terms && entity.terms.trim() && (
                  <>
                    <div className="inv-b">Terms &amp; Conditions</div>
                    <div className="inv-terms-body">{entity.terms}</div>
                  </>
                )}
              </td>
              <td className="inv-c">
                <div className="inv-b">Company Seal</div>
                {entity.sealUrl ? (
                  <img
                    src={entity.sealUrl}
                    alt="Company seal"
                    className="inv-seal"
                    style={{ objectFit: 'contain', margin: '0 auto' }}
                  />
                ) : (
                  <div className="inv-seal" />
                )}
              </td>
            </tr>

            {/* Auto-generated system message, centered at the bottom. */}
            <tr>
              <td colSpan={4} className="inv-note inv-c">
                This is a system-generated {isProforma ? 'proforma ' : ''}invoice and does not
                require a signature.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Admin panel (never printed): ledger + record payment + activity log. */}
      <InvoiceAdminPanel inv={inv} />

      {/* Credit notes (never printed): only meaningful once the tax invoice is issued. */}
      <CreditNotePanel invoiceId={inv.id} status={inv.status} />

      {/* Supplier AP settlement (never printed): payable lines + record payment. */}
      <SupplierSettlementPanel invoiceId={inv.id} />
    </div>
  );
};
